import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { AuthContext } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { pickPageCopy, roomCopy } from '../i18n/pageCopy';
import { getLocalizedError } from '../i18n/errorCopy';
import { translateSessionName } from '../i18n/sessionCopy';
import {
  canEnterRoom,
  formatDateTime,
  getProfile,
  getSessionById,
  listenMessages,
  parseSessionStart,
  sendChatMessage,
  sessionEnd,
} from '../services/marketplace';
import { connectSessionRoom } from '../services/webrtcRoom';
import '../style/Marketplace.css';

const formatCountdown = (target) => {
  if (!target) return '—';
  const diff = Math.max(0, target.getTime() - Date.now());
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const mediaErrorMessage = (error, copy) => {
  if (!navigator.mediaDevices?.getUserMedia) return copy.unsupported;
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
    return copy.blocked;
  }
  if (error?.name === 'NotFoundError') return copy.noDevice;
  if (error?.name === 'NotReadableError') return copy.inUse;
  return copy.mediaError;
};

const VirtualRoom = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useContext(AuthContext);
  const { language, t } = useI18n();
  const copy = pickPageCopy(language, roomCopy);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const streamRef = useRef(null);
  const connectionCleanupRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mediaError, setMediaError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [remoteReady, setRemoteReady] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioOnly, setAudioOnly] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [callStatus, setCallStatus] = useState(copy.startStatus);
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const row = await getSessionById(id);
        if (!cancelled) setSession(row);
      } catch (err) {
        if (!cancelled) setError(getLocalizedError(err, language, copy.sessionError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [copy.sessionError, id, language]);

  useEffect(() => {
    if (!user?.uid) return;
    getProfile(user.uid).then(setProfile).catch(() => null);
  }, [user?.uid]);

  useEffect(() => {
    if (!session?.id) return undefined;
    return listenMessages(
      session.id,
      setMessages,
      (err) => setError(getLocalizedError(err, language, copy.messageError)),
    );
  }, [copy.messageError, language, session?.id]);

  const roomAccess = canEnterRoom(session || {});
  const participantOk = useMemo(() => {
    if (!user || !session) return false;
    const email = String(user.email || '').toLowerCase();
    return user.uid === session.userId || user.uid === session.tellerId || email === String(session.clientEmail || '').toLowerCase() || email === String(session.tellerEmail || session.tutorEmail || '').toLowerCase() || email === String(session.bookedBy || '').toLowerCase();
  }, [user, session]);

  const isTeller = useMemo(() => {
    if (!user || !session) return false;
    const email = String(user.email || '').toLowerCase();
    return user.uid === session.tellerId
      || email === String(session.tellerEmail || session.tutorEmail || '').toLowerCase()
      || (profile?.role === 'teller' && user.uid !== session.userId);
  }, [profile?.role, session, user]);

  const end = useMemo(() => sessionEnd(session), [session]);
  const start = useMemo(() => parseSessionStart(session), [session]);
  const countdownTarget = roomAccess.ok ? end : start;

  const attachRemoteStream = async (stream) => {
    if (!remoteVideoRef.current) return;
    remoteVideoRef.current.srcObject = stream;
    remoteVideoRef.current.muted = false;
    remoteVideoRef.current.volume = 1;
    setRemoteReady(true);
    try {
      await remoteVideoRef.current.play();
      setPlaybackBlocked(false);
    } catch (playError) {
      setPlaybackBlocked(true);
    }
  };

  const startCall = async () => {
    if (streamRef.current || !roomAccess.ok || !user || !session) return;
    setMediaError('');
    setError('');
    setCallStatus(copy.requesting);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (cameraAndMicError) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        setAudioOnly(true);
        setVideoEnabled(false);
        setMediaError(copy.cameraFallback);
      } catch (audioError) {
        setMediaError(mediaErrorMessage(audioError || cameraAndMicError, copy));
        setCallStatus(copy.mediaMissing);
        return;
      }
    }

    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) {
      stream.getTracks().forEach((track) => track.stop());
      setMediaError(copy.noMic);
      setCallStatus(copy.micMissing);
      return;
    }

    streamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
      localVideoRef.current.play().catch(() => null);
    }
    setCameraReady(true);
    setAudioEnabled(audioTracks.some((track) => track.enabled));
    setVideoEnabled(stream.getVideoTracks().some((track) => track.enabled));

    try {
      connectionCleanupRef.current = await connectSessionRoom({
        sessionId: session.id,
        userId: user.uid,
        isInitiator: isTeller,
        localStream: stream,
        onRemoteStream: attachRemoteStream,
        onStatus: setCallStatus,
        onError: (connectionError) => {
          setError(getLocalizedError(connectionError, language, copy.connectionCompleteError));
        },
      });
    } catch (connectionError) {
      setError(getLocalizedError(connectionError, language, copy.connectionStartError));
      setCallStatus(copy.connectionFailed);
    }
  };

  const toggleAudio = () => {
    const tracks = streamRef.current?.getAudioTracks?.() || [];
    if (!tracks.length) return;
    const enabled = !tracks[0].enabled;
    tracks.forEach((track) => { track.enabled = enabled; });
    setAudioEnabled(enabled);
  };

  const toggleVideo = () => {
    const tracks = streamRef.current?.getVideoTracks?.() || [];
    if (!tracks.length) return;
    const enabled = !tracks[0].enabled;
    tracks.forEach((track) => { track.enabled = enabled; });
    setVideoEnabled(enabled);
  };

  const enableRemoteAudio = async () => {
    if (!remoteVideoRef.current) return;
    remoteVideoRef.current.muted = false;
    remoteVideoRef.current.volume = 1;
    try {
      await remoteVideoRef.current.play();
      setPlaybackBlocked(false);
    } catch (playError) {
      setError(copy.playbackError);
    }
  };

  useEffect(() => {
    return () => {
      connectionCleanupRef.current?.();
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    };
  }, []);

  const submitMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !user || !session) return;
    try {
      await sendChatMessage({ sessionId: session.id, senderId: user.uid, text });
      setText('');
    } catch (err) {
      setError(getLocalizedError(err, language, copy.messageError));
    }
  };

  if (authLoading || loading) {
    return <><Header /><main className="market-page"><div className="empty-card">{copy.loading}</div></main><Footer /></>;
  }

  if (!user) {
    return <><Header /><main className="market-page"><div className="empty-card">{copy.loginBefore} <Link to="/login">{copy.login}</Link></div></main><Footer /></>;
  }

  if (!session) {
    return <><Header /><main className="market-page"><div className="empty-card">{copy.notFound}</div></main><Footer /></>;
  }

  if (!participantOk) {
    return <><Header /><main className="market-page"><div className="market-alert">{copy.denied}</div></main><Footer /></>;
  }

  return (
    <div>
      <Header />
      <main className="market-page room-page">
        <section className="room-header">
          <div>
            <p className="eyebrow">{copy.private}</p>
            <h1>{translateSessionName(session.sessionName, t, copy.reading)}</h1>
            <p>{formatDateTime(session)} · {session.duration || 60} min · {session.tellerName || session.clientName || copy.live}</p>
          </div>
          <div className="room-timer"><span>{roomAccess.ok ? copy.ends : copy.opens}</span><strong>{formatCountdown(countdownTarget)}</strong></div>
        </section>

        {error && <div className="market-alert" role="alert">{error}</div>}
        {!roomAccess.ok && <div className="market-alert">{roomAccess.label}</div>}

        <section className="room-layout">
          <article className="video-panel">
            <div className="video-stage">
              <video ref={remoteVideoRef} autoPlay playsInline className={`remote-video ${remoteReady ? 'ready' : ''}`} />
              {!remoteReady && <div className="video-placeholder"><strong>{cameraReady ? copy.waiting : copy.room}</strong><span>{cameraReady ? callStatus : copy.joinHint}</span></div>}
              <div className={`local-preview ${cameraReady ? 'ready' : ''} ${videoEnabled ? '' : 'video-off'}`}>
                <video ref={localVideoRef} autoPlay muted playsInline />
                {cameraReady && !videoEnabled && <span>{audioEnabled ? copy.micOn : copy.micMuted}</span>}
              </div>
              <div className={`connection-badge ${remoteReady ? 'connected' : ''}`}><span aria-hidden="true" />{callStatus}</div>
            </div>
            <div className="room-controls" aria-label={t('a11y.callControls')}>
              <button type="button" onClick={startCall} disabled={!roomAccess.ok || cameraReady}>{cameraReady ? copy.mediaConnected : copy.joinMedia}</button>
              {cameraReady && <button type="button" className={audioEnabled ? '' : 'control-off'} onClick={toggleAudio}>{audioEnabled ? copy.mute : copy.unmute}</button>}
              {cameraReady && !audioOnly && <button type="button" className={videoEnabled ? '' : 'control-off'} onClick={toggleVideo}>{videoEnabled ? copy.cameraOff : copy.cameraOn}</button>}
              {playbackBlocked && <button type="button" className="sound-button" onClick={enableRemoteAudio}>{copy.enableSound}</button>}
              <Link to={profile?.role === 'teller' ? '/teller-dashboard' : '/user-dashboard'}>{copy.leave}</Link>
            </div>
            {mediaError && <p className="room-media-message" role="status">{mediaError}</p>}
            {cameraReady && <p className="room-media-state" role="status"><strong>{copy.microphone}: {audioEnabled ? copy.on : copy.muted}</strong><span>{copy.camera}: {videoEnabled ? copy.on : audioOnly ? copy.unavailable : copy.off}</span></p>}
          </article>

          <aside className="chat-panel">
            <div className="section-title-row"><h2>{copy.chat}</h2><span>{messages.length}</span></div>
            <div className="chat-messages">
              {messages.length === 0 ? <p className="dashboard-empty">{copy.noMessages}</p> : messages.map((message) => (
                <div key={message.id} className={`chat-bubble ${message.senderId === user.uid ? 'mine' : ''}`}>
                  <p>{message.text}</p>
                  <small>{message.senderId === user.uid ? copy.you : copy.participant}</small>
                </div>
              ))}
            </div>
            <form className="chat-form" onSubmit={submitMessage}>
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={copy.placeholder} disabled={!roomAccess.ok} />
              <button type="submit" disabled={!roomAccess.ok || !text.trim()}>{copy.send}</button>
            </form>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VirtualRoom;
